import {
    flexRender,
    getCoreRowModel,
    useReactTable,
    ColumnDef,
} from '@tanstack/react-table';
import { useState, useRef, useEffect } from 'react';

/* ============================================================
   Shared sensor result table (A pattern look).
   Used by every SensorLog_*_Result wrapper — pass the model's
   column keys; this handles styling, column toggle, RSSI bar.
   ============================================================ */

type SensorResultTableProps = {
    rows: any[];
    columnKeys: string[];
};

/* columns that should render in mono / be shortened */
const MONO = new Set(['macaddress', 'detected', 'arrived', 'app_id', 'manufacturer_id']);
/* columns that align right (numeric) */
const NUM = new Set(['no', 'battery', 'rssi', 'distance', 'temperature', 'humidity', 'count']);

const rssiColor = (rssi: number) => (rssi >= -60 ? '#10b981' : rssi >= -75 ? '#f59e0b' : '#ef4444');
const rssiPct = (rssi: number) => Math.round(((Math.max(-100, Math.min(-30, rssi)) + 100) / 70) * 100);

const RssiCell = ({ value }: { value: unknown }) => {
    if (value == null || value === '') return <span style={{ color: '#a1a1aa' }}>—</span>;
    const rssi = Number(value);
    return (
        <span className="bl-rssi">
            <span className="track"><span className="fill" style={{ width: rssiPct(rssi) + '%', background: rssiColor(rssi) }} /></span>
            <span className="val" style={{ color: rssiColor(rssi) }}>{rssi}</span>
        </span>
    );
};

const SensorResultTable = (props: SensorResultTableProps) => {
    const data = props.rows;

    const columns: ColumnDef<any, any>[] = props.columnKeys.map((key) => {
        if (key === 'rssi') {
            return { header: 'rssi', accessorKey: 'rssi', cell: c => <RssiCell value={c.getValue()} /> };
        }
        if (MONO.has(key)) {
            return {
                header: key, accessorKey: key,
                cell: c => {
                    const v = c.getValue();
                    if (v == null || v === '') return <span style={{ color: '#a1a1aa' }}>—</span>;
                    // show full value (no shortening); mono font keeps it readable
                    return <span className="bl-cell-mono">{String(v)}</span>;
                },
            };
        }
        return { header: key, accessorKey: key };
    });

    const table = useReactTable({ columns, data, getCoreRowModel: getCoreRowModel() });

    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);
    const visibleCount = table.getVisibleLeafColumns().length;

    return (
        <div className="bl-result">
            <div className="bl-result-bar">
                <span className="bl-result-count">このページ <b>{data.length}</b> 件</span>
                <div className="bl-toolbar">
                    <div className="bl-pop-wrap" ref={ref}>
                        <button type="button" className="bl-btn sm" onClick={() => setOpen(o => !o)}>
                            <ColIcon /> Columns <span style={{ color: '#a1a1aa', fontFamily: 'var(--bl-mono)' }}>{visibleCount}</span>
                        </button>
                        {open && (
                            <div className="bl-pop">
                                <div className="bl-pop-title">表示する項目</div>
                                {table.getAllLeafColumns().map(column => (
                                    <label key={column.id} className="bl-pop-item">
                                        <input type="checkbox" checked={column.getIsVisible()} onChange={column.getToggleVisibilityHandler()} />
                                        {column.id}
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="bl-table-scroll">
                <table className="bl-table">
                    <thead>
                        {table.getHeaderGroups().map((hg) => (
                            <tr key={hg.id}>
                                {hg.headers.map((header) => (
                                    <th key={header.id} className={NUM.has(header.column.id) ? 'num' : ''}>
                                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map((row) => (
                            <tr key={row.id}>
                                {row.getVisibleCells().map((cell) => (
                                    <td key={cell.id} className={(NUM.has(cell.column.id) ? 'num ' : '') + (MONO.has(cell.column.id) ? 'bl-cell-mono' : '')}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const ColIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 3v18" /><path d="M15 3v18" />
    </svg>
);

export default SensorResultTable;
